/*
  Product rail carousel, shared by sections/ctx-best-selling and
  sections/ctx-new-arrivals-rail. It lived inline in both sections until
  12 Oct 2026; as an asset the home page carries it once, cached, instead of
  twice in the HTML.

  Page-speed changes, same date (the home page was 5,584 DOM elements):
  - Only the tab that is showing gets its carousel built on load. Building one
    clones every card twice for the endless loop, and doing that for every
    hidden tab up front was ~1,400 elements nobody could see. A hidden tab is
    built the first time it is clicked, once it is visible and measurable.
  - The animation loop sleeps once the strip has settled instead of running
    every frame forever (one loop per tab, so seven on the home page).
  - Add-to-cart is one delegated listener, so cards cloned later still work.
*/
(function() {
  'use strict';
  /* Both rails include this file; run it once. */
  if (window.__bsfgRail) return;
  window.__bsfgRail = true;

  function initBSFGSection(sectionEl) {
    /* Run once per section: ctx-best-selling and ctx-new-arrivals-rail share this
       script, and each copy scans every .bsfg-section on load (4 Oct 2026). */
    if (sectionEl.getAttribute('data-bsfg-init')) return;
    sectionEl.setAttribute('data-bsfg-init', '1');
    var AUTO_MS = 3000;
    var EASE    = 0.16;   // firmer than 0.08 - see tick() for why

    var tabs   = sectionEl.querySelectorAll('.bsfg-tab');
    var panels = sectionEl.querySelectorAll('.bsfg-panel');

    var panelResetFns = {};

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        tabs.forEach(function(t) {
          t.classList.remove('bsfg-tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        panels.forEach(function(p) { p.classList.remove('bsfg-panel--active'); });
        tab.classList.add('bsfg-tab--active');
        tab.setAttribute('aria-selected', 'true');
        var panel = sectionEl.querySelector('#bsfg-panel-' + tab.dataset.tabId);
        if (panel) {
          panel.classList.add('bsfg-panel--active');
          initPanel(panel);   // first visit to this tab builds it
          // Reset carousel to position 0 when switching tabs
          var resetFn = panelResetFns[panel.id];
          if (resetFn) resetFn();
        }
      });
    });

    function initPanel(panel) {
      if (panel.__bsfgInit) return;
      panel.__bsfgInit = true;
      var track   = panel.querySelector('[data-carousel-track]');
      var wrapper = panel.querySelector('.bsfg-carousel-wrapper');
      var dotBtns = Array.from(panel.querySelectorAll('.bsfg-dot'));
      var prevBtn = panel.querySelector('[data-carousel-prev]');
      var nextBtn = panel.querySelector('[data-carousel-next]');
      if (!track || !wrapper) return;

      var DOTS = dotBtns.length;
      var origCards = Array.from(track.children);
      var ORIG = origCards.length;
      if (!ORIG) return;

      origCards.forEach(function(c) { track.appendChild(c.cloneNode(true)); });
      origCards.slice().reverse().forEach(function(c) { track.insertBefore(c.cloneNode(true), track.firstChild); });

      var currentX = 0, targetX = 0, logicalIdx = 0;
      var rafId = null, autoTimer = null;
      var isDragging = false, dragStartX = 0, dragStartTarget = 0;

      var dragMoved = false;
      var DRAG_THRESHOLD = 6; // px — below this distance it's treated as a click

      function allCards() { return Array.from(track.children); }
      function cardW()    { return (allCards()[0] || {offsetWidth:302}).offsetWidth; }
      function gapW()     { return parseFloat(getComputedStyle(track).gap) || 16; }
      function stride()   { return cardW() + gapW(); }

      function centreConst()     { return (window.innerWidth - cardW()) / 2; }
      function centreOffset(idx) { return idx * stride() - centreConst(); }
      function xForSlot(idx)     { return -centreOffset(idx); }
      function slotFromX(x)      { return Math.round((centreConst() - x) / stride()); }

      function updateDots() {
        dotBtns.forEach(function(d, i) {
          d.classList.toggle('bsfg-dot--active', i === logicalIdx % DOTS);
        });
      }

      /*
        absSlot is a real slot in the rendered strip - [clones][originals][clones]
        - not an index folded down into 0..ORIG-1.

        It used to be folded. Every move recomputed the target as
        ORIG + (idx mod ORIG), which always lands inside the middle copy, so
        stepping from the last card round to the first moved the target BACKWARD
        by ORIG-1 cards. tick() eases toward the target rather than jumping, so
        that rewind played out on screen: the row visibly slid all the way back
        to where it started instead of carrying on in one direction.

        Now the slot only ever moves the way it was asked to. reseat() puts it
        back in the middle copy once the motion has stopped, which is invisible
        because every copy holds the same cards in the same order.
      */
      var absSlot = ORIG;

      function applyTarget(instant) {
        targetX = xForSlot(absSlot);
        if (instant) {
          currentX = targetX;
          track.style.transform = 'translateX(' + currentX + 'px)';
        }
        logicalIdx = ((absSlot % ORIG) + ORIG) % ORIG;
        updateDots();
        kick();
        scheduleAuto();
      }

      /* delta is a number of cards, signed. Never folded, so never rewinds. */
      function advance(delta, instant) {
        absSlot += delta;
        applyTarget(instant);
      }

      /*
        Settle after a drag, taking the gesture's SPEED into account.

        Snapping purely to the nearest slot meant a card only changed once the
        drag passed half a card width, so short flicks sprang straight back and
        the strip felt glued to the screen.

        A flick now always carries at least one card past where it started, and
        even a slow drag commits at a quarter of a card. Capped at two cards so a
        hard throw stays predictable.
      */
      var FLICK_VELOCITY  = 0.28;   // px/ms - above this it is a toss, not a nudge
      var FAST_VELOCITY   = 0.95;   // hard flick, worth two cards
      var COMMIT_FRACTION = 0.25;   // fraction of a card a slow drag must cover

      function settleFromDrag(startSlotAtDown, travel, velocity) {
        var slot = slotFromX(currentX);

        if (velocity !== undefined && Math.abs(velocity) >= FLICK_VELOCITY) {
          var dir = velocity < 0 ? 1 : -1;   // leftward drag advances
          /* Two cards needs speed AND distance - a fast flick of the thumb across
             75px should not throw the strip twice as far as it travelled. */
          var jump = (Math.abs(velocity) >= FAST_VELOCITY
                      && travel !== undefined
                      && Math.abs(travel) >= stride() * 0.4) ? 2 : 1;
          var flicked = startSlotAtDown + (dir * jump);
          if (dir > 0) slot = Math.max(slot, flicked);
          else         slot = Math.min(slot, flicked);
        } else if (travel !== undefined
                   && Math.abs(travel) >= stride() * COMMIT_FRACTION
                   && slot === startSlotAtDown) {
          slot = startSlotAtDown + (travel < 0 ? 1 : -1);
        }

        absSlot = slot;
        applyTarget(false);
      }

      /* Dots address a specific card, so go whichever way round is shorter
         instead of always counting upward. */
      function goToDot(i) {
        var want = ((i % ORIG) + ORIG) % ORIG;
        var cur  = ((absSlot % ORIG) + ORIG) % ORIG;
        var d    = want - cur;
        if (d >  ORIG / 2) d -= ORIG;
        if (d < -ORIG / 2) d += ORIG;
        advance(d);
      }

      /* Fold absSlot back into the middle copy so the clone buffer on either
         side never runs out. Only safe once easing has finished - doing it
         mid-glide would snap the strip in front of the user. */
      function reseat() {
        var moved = false;
        while (absSlot <  ORIG)     { absSlot += ORIG; moved = true; }
        while (absSlot >= ORIG * 2) { absSlot -= ORIG; moved = true; }
        if (!moved) return;
        targetX = currentX = xForSlot(absSlot);
        track.style.transform = 'translateX(' + currentX + 'px)';
      }

      /*
        translate3d and a shorter tail.

        Two things made this feel slightly laggy rather than smooth. A 2D
        translateX can be composited on the main thread, so every frame queues
        behind whatever else is running; the Z component puts the track on its own
        GPU layer. And an exponential ease at 0.08 spends a long time covering the
        last few pixels - technically still moving, but slow enough to read as drag.
        A firmer factor plus a wider snap threshold ends the movement crisply
        instead of trailing off.
      */
      function tick() {
        if (!isDragging) {
          var diff = targetX - currentX;
          if (Math.abs(diff) > 0.5) {
            currentX += diff * EASE;
          } else if (currentX !== targetX) {
            currentX = targetX;
            reseat();
          } else {
            rafId = null;   // settled: sleep until kick() wakes the loop
            return;
          }
          track.style.transform = 'translate3d(' + currentX + 'px,0,0)';
        }
        rafId = requestAnimationFrame(tick);
      }
      function kick() {
        if (!rafId) rafId = requestAnimationFrame(tick);
      }

      /*
        Autoplay is OFF by design.

        Nothing calls advance() on a timer any more. Every scheduleAuto() call site
        is left in place so the flow still reads correctly and re-enabling is a
        one-line change, but the timer is simply never armed. Movement now only
        happens when the customer asks for it - arrows, dots, or a swipe.
      */
      var AUTO_PLAY = false;

      function scheduleAuto() {
        clearTimeout(autoTimer);
        if (!AUTO_PLAY) return;
        autoTimer = setTimeout(function() { advance(1); }, AUTO_MS);
      }
      function pauseAuto() { clearTimeout(autoTimer); }

      panelResetFns[panel.id] = function() { absSlot = ORIG; applyTarget(true); };

      if (prevBtn) prevBtn.addEventListener('click', function() { advance(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function() { advance(1); });
      dotBtns.forEach(function(d, i) { d.addEventListener('click', function() { goToDot(i); }); });

      /* Two samples of the gesture, so release can read a speed and not just a
         final resting position. */
      var gMoveX = 0, gMoveT = 0, gPrevX = 0, gPrevT = 0, gStartSlot = 0;

      function sample(x) {
        gPrevX = gMoveX; gPrevT = gMoveT;
        gMoveX = x;      gMoveT = Date.now();
      }
      function gestureVelocity() {
        var dt = gMoveT - gPrevT;
        /* A finger that paused before lifting is placing, not throwing. */
        if (!dt || dt > 120) return 0;
        if (Date.now() - gMoveT > 140) return 0;
        return (gMoveX - gPrevX) / dt;
      }

      /* The arrows live inside the wrapper now, and the wrapper is the drag
         surface. Without this a press on an arrow starts a drag, which is what
         broke the identical buttons on the category slider. */
      function onArrow(e) {
        return !!(e.target.closest && e.target.closest('.bsfg-arrow'));
      }

      wrapper.addEventListener('mousedown', function(e) {
        if (onArrow(e)) return;
        isDragging = true;
        dragMoved = false;
        dragStartX = e.clientX;
        dragStartTarget = targetX;
        gStartSlot = slotFromX(currentX);
        gMoveX = gPrevX = e.clientX;
        gMoveT = gPrevT = Date.now();
        pauseAuto();
      });

      window.addEventListener('mousemove', function(e) {
        if (!isDragging) return;
        var dx = e.clientX - dragStartX;
        // Only start visually dragging once threshold is exceeded
        if (!dragMoved && Math.abs(dx) < DRAG_THRESHOLD) return;
        dragMoved = true;
        wrapper.classList.add('is-dragging');
        sample(e.clientX);
        currentX = targetX = dragStartTarget + dx;
        track.style.transform = 'translateX(' + currentX + 'px)';
      });

      window.addEventListener('mouseup', function(e) {
        if (!isDragging) return;
        isDragging = false;
        wrapper.classList.remove('is-dragging');
        // FIX: if pointer barely moved, it's a click — let the anchor navigate naturally
        if (!dragMoved) return;
        settleFromDrag(gStartSlot, e.clientX - dragStartX, gestureVelocity());
      });

      /* Axis locking on touch.
         This previously read only clientX, so any touch with a sideways
         component dragged the carousel - and the listeners are passive, so the
         page scrolled at the same time. Nobody swipes perfectly vertically, so
         scrolling past this row grabbed the products.

         The first few pixels now decide who owns the gesture: mostly vertical
         and the carousel ignores the touch so the page scrolls cleanly, mostly
         horizontal and the carousel takes it. Decided once per touch so it
         cannot flip halfway through. */
      var AXIS_THRESHOLD = 8;
      var touchStart  = 0;
      var touchStartY = 0;
      var touchAxis   = null;   // null = undecided, 'x' = carousel, 'y' = page

      wrapper.addEventListener('touchstart', function(e) {
        if (onArrow(e)) { touchAxis = 'ignore'; return; }
        touchStart      = e.touches[0].clientX;
        touchStartY     = e.touches[0].clientY;
        touchAxis       = null;
        dragStartTarget = targetX;
        gStartSlot      = slotFromX(currentX);
        gMoveX = gPrevX = touchStart;
        gMoveT = gPrevT = Date.now();
        pauseAuto();
      }, { passive: true });

      wrapper.addEventListener('touchmove', function(e) {
        if (touchAxis === 'y' || touchAxis === 'ignore') return;   // page scroll, or a press on an arrow

        var x = e.touches[0].clientX;
        var y = e.touches[0].clientY;

        if (touchAxis === null) {
          var adx = Math.abs(x - touchStart);
          var ady = Math.abs(y - touchStartY);
          if (adx < AXIS_THRESHOLD && ady < AXIS_THRESHOLD) return;   // too early to tell
          touchAxis = ady > adx ? 'y' : 'x';
          if (touchAxis === 'y') { scheduleAuto(); return; }
        }

        sample(x);
        currentX = targetX = dragStartTarget + (x - touchStart);
        track.style.transform = 'translateX(' + currentX + 'px)';
      }, { passive: true });

      wrapper.addEventListener('touchend', function(e) {
        if (touchAxis !== 'x') { touchAxis = null; return; }   // was a scroll, not a swipe
        touchAxis = null;
        /* A tap still follows its link - nothing here cancels the click - and the
           gesture's speed decides whether a short swipe commits or springs back. */
        var endX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : gMoveX;
        settleFromDrag(gStartSlot, endX - touchStart, gestureVelocity());
      });

      wrapper.addEventListener('mouseenter', pauseAuto);
      wrapper.addEventListener('mouseleave', function() { if (!isDragging) scheduleAuto(); });
      window.addEventListener('resize', function() { applyTarget(true); });

      applyTarget(true);

      /*
        Measure again once the page has actually finished loading.

        Every position here is derived from the width of a card, and this script
        runs inline while the document is still parsing. Measured in the browser:
        the first card reported 97px instead of 302px, because the section's own
        stylesheet had not been applied yet - so the strip parked at -85px instead
        of -1419px and sat visibly off-centre. Nothing looked broken, because every
        card is a clone of every other card, but the first press of an arrow
        recomputed the target against the real width and threw the row sideways by
        about 1600px in one go.

        A ResizeObserver on the wrapper covers the same class of problem later on -
        a web font landing, or the panel being revealed by a tab switch.
      */
      window.addEventListener('load', function() { applyTarget(true); });

      if (window.ResizeObserver) {
        var lastW = 0;
        new ResizeObserver(function() {
          var w = track.children[0] ? track.children[0].offsetWidth : 0;
          if (!w || w === lastW) return;   // ignore the noise; only a real resize matters
          lastW = w;
          applyTarget(true);
        }).observe(wrapper);
      }

      kick();
      scheduleAuto();
    }

    /* Build the tab that is showing now; the rest wait for their first click. */
    panels.forEach(function(panel) {
      if (panel.classList.contains('bsfg-panel--active')) initPanel(panel);
    });

    sectionEl.addEventListener('click', function(e) {
      var btn = e.target.closest && e.target.closest('.bsfg-btn-cart');
      if (!btn || !sectionEl.contains(btn)) return;
      (function() {
        var variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.disabled = true;
        btn.classList.add('bsfg-btn-cart--loading');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
        .then(function(r) { return r.json(); })
        .then(function() {
          btn.classList.remove('bsfg-btn-cart--loading');
          btn.classList.add('bsfg-btn-cart--added');
          document.dispatchEvent(new CustomEvent('cart:updated'));
          setTimeout(function() {
            btn.classList.remove('bsfg-btn-cart--added');
            btn.disabled = false;
          }, 2000);
        })
        .catch(function() {
          btn.disabled = false;
          btn.classList.remove('bsfg-btn-cart--loading');
        });
      })();
    });
  }

  // Init on page load
  document.querySelectorAll('.bsfg-section').forEach(initBSFGSection);

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', function(e) {
    var sectionEl = e.target.querySelector('.bsfg-section');
    if (sectionEl) initBSFGSection(sectionEl);
  });

})();
